import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';

import {
  Button,
  DateField,
  Modal,
  SingleSelectField,
  Text,
  TextAreaField,
  TextField,
} from '../../common/components';
import { t } from '../../common/i18n';
import Yup from '../../common/utils/FormValidation';
import { categoryOptions, priorityOptions } from '../config/deadlineCategories';
import { deadlineTemplates, DeadlineTemplate } from '../config/deadlineTemplates';
import { Deadline, DeadlineCategory, DeadlineFormValues, DeadlinePriority } from '../types';

const namespace = 'Deadlines';

type ModalType = 'form' | 'delete';

interface DeadlineFormModalProps {
  isShown: boolean;
  modalType: ModalType;
  selected: Deadline | null;
  onHide: () => void;
  onSubmit: (values: DeadlineFormValues, id: string | null) => Promise<void>;
  onDelete: (id: string) => void;
}

const validationSchema = Yup.object({
  title: Yup.string().required(),
  dueDate: Yup.string().required(),
  category: Yup.string().required(),
});

export const DeadlineFormModal: React.FC<DeadlineFormModalProps> = ({
  isShown,
  modalType,
  selected,
  onHide,
  onSubmit,
  onDelete,
}) => {
  const isEdit = selected !== null;

  const formik = useFormik<DeadlineFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: selected?.title ?? '',
      note: selected?.note ?? '',
      dueDate: selected?.dueDate ?? '',
      category: selected?.category ?? '',
      priority: selected?.priority ?? 'normal',
    },
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values, selected?.id ?? null);
      formik.setSubmitting(false);
    },
  });

  const categories = useMemo(() => categoryOptions(), []);
  const priorities = useMemo(() => priorityOptions(), []);

  const templateOptions = useMemo(
    () => deadlineTemplates.map((template) => ({ value: template.key, label: template.title })),
    []
  );
  const [templateKey, setTemplateKey] = useState<string | null>(null);

  useEffect(() => {
    if (isShown) setTemplateKey(null);
  }, [isShown]);

  const applyTemplate = (key: string | null) => {
    setTemplateKey(key);
    const template: DeadlineTemplate | undefined = deadlineTemplates.find((item) => item.key === key);
    if (!template) return;

    formik.setValues({
      ...formik.values,
      title: template.title,
      note: template.note,
      category: template.category,
      priority: template.priority,
    });
  };

  const categoryValue = categories.find((option) => option.value === formik.values.category) ?? null;
  const priorityValue = priorities.find((option) => option.value === formik.values.priority) ?? null;
  const templateValue = templateOptions.find((option) => option.value === templateKey) ?? null;

  return (
    <Modal isShown={isShown} onHide={onHide}>
      {modalType === 'form' && (
        <>
          <Text size="h2" align="center" className="mb-3">
            {isEdit ? t('Modal.editTitle', namespace) : t('Modal.createTitle', namespace)}
          </Text>

          <form onSubmit={formik.handleSubmit}>
            {!isEdit && (
              <SingleSelectField
                label={t('Modal.Fields.template.label', namespace)}
                placeholder={t('Modal.Fields.template.placeholder', namespace)}
                name="template"
                value={templateValue}
                onChange={(option) => applyTemplate((option as { value: string } | null)?.value ?? null)}
                options={templateOptions}
                isClearable
                className="mb-2"
              />
            )}

            <TextField
              label={t('Modal.Fields.title.label', namespace)}
              placeholder={t('Modal.Fields.title.placeholder', namespace)}
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={{ message: formik.errors.title, touched: formik.touched.title }}
              className="mb-2"
              required
            />

            <DateField
              label={t('Modal.Fields.dueDate.label', namespace)}
              name="dueDate"
              value={formik.values.dueDate}
              onChange={(date) => formik.setFieldValue('dueDate', date)}
              error={{ message: formik.errors.dueDate, touched: formik.touched.dueDate }}
              className="mb-2"
              required
            />

            <SingleSelectField
              label={t('Modal.Fields.category.label', namespace)}
              placeholder={t('Modal.Fields.category.placeholder', namespace)}
              name="category"
              value={categoryValue}
              onChange={(option) =>
                formik.setFieldValue('category', (option as { value: DeadlineCategory } | null)?.value ?? '')
              }
              onBlur={() => formik.setFieldTouched('category', true)}
              options={categories}
              error={{ message: formik.errors.category, touched: formik.touched.category }}
              className="mb-2"
              required
            />

            <SingleSelectField
              label={t('Modal.Fields.priority.label', namespace)}
              name="priority"
              value={priorityValue}
              onChange={(option) =>
                formik.setFieldValue('priority', (option as { value: DeadlinePriority } | null)?.value ?? 'normal')
              }
              options={priorities}
              className="mb-2"
            />

            <TextAreaField
              label={t('Modal.Fields.note.label', namespace)}
              placeholder={t('Modal.Fields.note.placeholder', namespace)}
              name="note"
              value={formik.values.note}
              onChange={formik.handleChange}
              className="mb-3"
            />

            <Button
              onClick={formik.handleSubmit}
              loading={formik.isSubmitting}
              disabledDeps={['demo', 'expired']}
              wide
              title={
                isEdit ? t('Modal.Fields.submitEdit', namespace) : t('Modal.Fields.submitCreate', namespace)
              }
            />
          </form>
        </>
      )}

      {modalType === 'delete' && selected && (
        <>
          <Text size="h2" align="center" className="mb-2">
            {t('Modal.deleteTitle', namespace)}
          </Text>
          <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
            {t('Modal.deleteConfirmText', namespace)}
          </Text>
          <div className="Flex FlexColumn FlexAlign--center">
            <Button
              title={t('Modal.deleteConfirm', namespace)}
              variant="primary-light"
              wide
              onClick={() => onDelete(selected.id)}
              disabledDeps={['demo', 'expired']}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

export default DeadlineFormModal;
